import { Box, Grid, SxProps, Theme } from '@mui/material';
import { amber, green, red } from '@mui/material/colors';
import { InfoLabel } from 'quote-detail/info-label';
import { NumberTextField } from 'shared/controls';
import { CompetitionOptions, Customer, ProductDetailMetric, Quote } from 'shared/types';
import { formatDate } from '../shared/utility';
import { QuoteExpirationDate } from '../new/quote-expiration-date';

const rootStyles: SxProps<Theme> = (theme: Theme) => ({
  marginBottom: theme.spacing(5),
  '& h6': {
    fontWeight: 'bold',
  },
  '& .MuiFormLabel-root.Mui-disabled': {
    color: theme.palette.secondary.contrastText,
  },
  '& .MuiInputBase-root': {
    fontWeight: 'bold',
    color: theme.palette.secondary.contrastText,
  },
  '& .MuiInputAdornment-positionStart': {
    marginRight: 0,
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderBottomColor: theme.palette.secondary.main,
    borderBottomWidth: '5px',
  },
  '& p': {
    color: theme.palette.secondary.contrastText,
    fontWeight: 'bold',
  },
});
const competitionStyles = (competitionColor: string) => ({
  '& h6': {
    borderBottom: '5px solid',
    borderBottomColor: competitionColor,
    width: 'fit-content',
  },
});

interface CustomerInformationProps {
  customer: Customer;
  metrics: ProductDetailMetric[];
  quote: Quote;
  editMode: boolean;
}

export function CustomerInformation({ customer, metrics, quote, editMode }: CustomerInformationProps) {
  function getCompetitionColor(competition: CompetitionOptions): string {
    switch (competition) {
      case 'HIGH':
        return red[500];
      case 'LOW':
      case 'SPEC':
        return green[500];
      case 'MEDIUM':
        return amber[500];
      default:
        return '';
    }
  }
  const competitionColor = getCompetitionColor(customer.competition);

  return (
    <Box sx={rootStyles}>
      <Grid container justifyContent='space-between' alignItems='center'>
        <Grid item sx={competitionStyles(competitionColor)}>
          <InfoLabel label='Competition:' text={customer.competition} />
        </Grid>
        <Grid item>
          <InfoLabel label='Customer Tier:' text={customer.customerTier} />
        </Grid>
        <Grid item>
          <InfoLabel label='Type:' text={customer.customerType} />
        </Grid>
        {metrics.map((metric, i) => (
          <Grid item key={i}>
            <InfoLabel label={metric.label} text={metric.value} />
          </Grid>
        ))}
        {editMode ? (
          <Grid item>
            <QuoteExpirationDate initialDate={new Date(quote.expirationDate)} />
          </Grid>
        ) : (
          <Grid item>
            <InfoLabel
              label='Expiration Date'
              text={quote?.expirationDate ? formatDate(new Date(quote.expirationDate)) : ''}
            />
          </Grid>
        )}
        <NumberTextField prefix='$' name='quoteTotal' label='Quote Total' disabled fullWidth={false} />
      </Grid>
    </Box>
  );
}
